export const quantitativeMethodsCourse = {
  id: "quantitative-methods-finance-2024",
  title: "Quantitative Methods for Finance: Complete Professional Course",
  shortDescription: "Master statistical analysis, econometrics, and mathematical modeling techniques essential for modern finance and investment analysis.",
  description: "Comprehensive course covering probability, statistics, econometrics, and advanced quantitative techniques used in financial modeling, risk management, and investment analysis.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "advanced",
  duration: 22,
  estimatedWeeks: 11,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from quantitative analysts and financial engineers",
  thumbnailUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Apply probability theory and statistical methods to finance",
    "Build and validate econometric models for financial analysis",
    "Implement Monte Carlo simulation techniques",
    "Master time series analysis for financial data",
    "Develop optimization models for portfolio management",
    "Apply machine learning techniques to financial problems",
    "Understand stochastic processes in finance",
    "Build comprehensive financial risk models"
  ],

  prerequisites: [
    "Calculus and Linear Algebra",
    "Statistics Fundamentals",
    "Excel/Programming Skills",
    "Basic Finance Knowledge"
  ],

  skills: [
    "Statistical Analysis",
    "Econometric Modeling",
    "Monte Carlo Simulation",
    "Time Series Analysis",
    "Portfolio Optimization",
    "Risk Modeling",
    "Machine Learning",
    "Financial Programming"
  ],

  tags: [
    "Quantitative Finance",
    "Statistics",
    "Econometrics",
    "Financial Modeling",
    "Risk Analysis",
    "Monte Carlo",
    "Machine Learning",
    "Mathematical Finance"
  ],

  marketData: {
    averageSalary: 145000,
    salaryRange: {
      min: 95000,
      max: 250000
    },
    jobDemand: 4.8,
    marketTrend: "growing",
    careerPaths: [
      "Quantitative Analyst",
      "Risk Manager",
      "Financial Engineer", 
      "Algorithmic Trader",
      "Data Scientist",
      "Research Analyst"
    ],
    relatedSkills: [
      "Python/R",
      "SQL",
      "MATLAB",
      "Machine Learning",
      "Statistics",
      "Financial Markets"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "probability-statistics",
      title: "Probability and Statistics",
      description: "Probability distributions, hypothesis testing, and statistical inference",
      order: 1,
      estimatedHours: 5.0,
      content: [
        {
          id: "probability-basics",
          title: "Probability Theory Fundamentals",
          description: "Basic probability concepts and financial applications",
          type: "video",
          duration: 40
        },
        {
          id: "statistical-distributions",
          title: "Statistical Distributions in Finance",
          description: "Normal, lognormal, and other distributions used in finance",
          type: "finance-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "distribution-analysis",
            instructions: "Analyze different probability distributions and their applications in finance. Calculate key statistics and probabilities.",
            scenario: {
              distributions: [
                { name: "Normal", parameters: { mean: 0.08, stddev: 0.15 } },
                { name: "Lognormal", parameters: { mean: 0.08, stddev: 0.15 } },
                { name: "Student-t", parameters: { degrees: 5, mean: 0.08, scale: 0.15 } }
              ]
            },
            hints: [
              "Normal distribution is symmetric around the mean",
              "Lognormal is often used for stock prices (can't be negative)",
              "Student-t has fatter tails than normal distribution",
              "Consider skewness and kurtosis for real financial data"
            ]
          }
        }
      ],
      quiz: {
        id: "probability-quiz",
        title: "Probability and Statistics Quiz",
        description: "Test your understanding of probability and statistical concepts",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the key assumption of the Central Limit Theorem?",
            options: [
              "The population must be normally distributed",
              "Sample size must be greater than 30",
              "Samples are drawn independently from any distribution with finite variance",
              "The data must be continuous"
            ],
            correctAnswer: 2,
            explanation: "The Central Limit Theorem states that sample means approach a normal distribution regardless of the underlying population distribution, provided samples are independent and the population has finite variance."
          }
        ]
      }
    },

    {
      id: "econometrics",
      title: "Econometric Methods",
      description: "Regression analysis, model validation, and econometric applications",
      order: 2,
      estimatedHours: 5.5,
      content: [
        {
          id: "linear-regression",
          title: "Linear Regression and Model Building",
          description: "OLS estimation, model assumptions, and validation techniques",
          type: "video",
          duration: 45
        },
        {
          id: "regression-diagnostics",
          title: "Regression Diagnostics and Model Selection",
          description: "Testing assumptions, model specification, and variable selection",
          type: "finance-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "regression-analysis",
            instructions: "Build and validate a regression model predicting stock returns using multiple factors. Test model assumptions and interpret results.",
            scenario: {
              dependentVariable: "Stock Returns",
              independentVariables: ["Market Return", "Size Factor", "Value Factor", "Momentum Factor", "Interest Rate"],
              sampleSize: 120,
              timeFrameMonths: 10
            },
            hints: [
              "Check for multicollinearity between independent variables",
              "Test residuals for normality and homoscedasticity",
              "Consider economic significance vs statistical significance",
              "Validate model with out-of-sample testing"
            ]
          }
        },
        {
          id: "advanced-regression",
          title: "Advanced Regression Techniques",
          description: "Panel data, instrumental variables, and non-linear models",
          type: "text",
          duration: 55,
          textContent: `# Advanced Regression Techniques in Finance

## Panel Data Models

### Fixed Effects Models

**Fixed Effects Estimation:**
- **Individual Fixed Effects**: Control for time-invariant heterogeneity
- **Time Fixed Effects**: Control for common time trends
- **Two-Way Fixed Effects**: Both individual and time effects
- **Within Transformation**: Demeans variables to eliminate fixed effects

**Financial Applications:**
- **Firm-Level Analysis**: Control for unobserved firm characteristics
- **Country Studies**: Account for country-specific factors
- **Asset Pricing**: Control for security-specific effects
- **Performance Evaluation**: Manager or fund fixed effects

**Mathematical Framework:**
Yit = αi + Xitβ + δt + εit

Where:
- αi = individual fixed effect
- δt = time fixed effect
- Xit = explanatory variables
- εit = error term

### Random Effects Models

**Random Effects Assumptions:**
- Individual effects are random draws from distribution
- Effects uncorrelated with explanatory variables
- More efficient than fixed effects when assumptions hold
- Hausman test for choosing between fixed and random effects

**Generalized Least Squares (GLS):**
- **Feasible GLS**: Use estimated variance components
- **Between vs. Within Variation**: Utilizes both sources of variation
- **Efficiency Gains**: More efficient than fixed effects under correct specification
- **Assumption Testing**: Breusch-Pagan test for random effects

### Dynamic Panel Models

**Lagged Dependent Variables:**
- **Autoregressive Panels**: Yit = αYit-1 + Xitβ + μi + εit
- **Bias Issues**: Fixed effects estimator biased with lagged dependent variable
- **Instrumental Variables**: Use further lags as instruments

**Arellano-Bond Estimation:**
- **First Differencing**: Eliminates fixed effects
- **GMM Estimation**: Generalized Method of Moments
- **Instrument Sets**: Lagged levels as instruments for differences
- **Overidentification Tests**: Sargan test for instrument validity

## Instrumental Variables Methods

### Two-Stage Least Squares (2SLS)

**Endogeneity Problems:**
- **Simultaneity**: Y affects X while X affects Y
- **Omitted Variable Bias**: Correlated unobserved factors
- **Measurement Error**: Errors in explanatory variables
- **Sample Selection**: Non-random sample selection

**IV Requirements:**
- **Relevance**: Instruments correlated with endogenous variables
- **Exogeneity**: Instruments uncorrelated with error term
- **Exclusion Restriction**: Instruments affect outcome only through endogenous variable

**First Stage Regression:**
X = Zπ + v

Where:
- Z = instruments
- π = reduced form parameters
- v = first stage error

**Second Stage:**
Y = X̂β + u

Where X̂ are predicted values from first stage

### Weak Instruments Problem

**Diagnostic Tests:**
- **First Stage F-Test**: F > 10 rule of thumb for strong instruments
- **Stock-Yogo Critical Values**: Formal weak instrument tests
- **Anderson-Rubin Test**: Robust to weak instruments

**Financial Applications:**
- **Corporate Finance**: CEO characteristics as instruments for firm policies
- **International Finance**: Geographic variables for trade instruments
- **Asset Pricing**: Institutional features as instruments for ownership
- **Banking**: Regulatory changes as instruments for bank behavior

## Limited Dependent Variable Models

### Binary Choice Models

**Probit Model:**
- **Latent Variable Framework**: Y* = Xβ + ε, Y = 1 if Y* > 0
- **Normal Distribution**: ε ~ N(0,1)
- **Maximum Likelihood**: Non-linear estimation required
- **Marginal Effects**: ∂P/∂x = φ(Xβ)β

**Logit Model:**
- **Logistic Distribution**: F(z) = ez/(1+ez)
- **Odds Ratios**: exp(β) interpretation
- **Log-Odds**: ln(P/(1-P)) = Xβ

**Financial Applications:**
- **Credit Risk**: Probability of default models
- **IPO Decisions**: Firm going public decisions
- **Dividend Policy**: Dividend payment decisions
- **Market Participation**: Investor participation in markets

### Count Data Models

**Poisson Regression:**
- **Count Outcomes**: Number of events (acquisitions, issues)
- **Poisson Distribution**: P(Y=k) = e^(-λ)λ^k/k!
- **Mean-Variance Equality**: E[Y] = Var[Y] = λ
- **Link Function**: ln(λ) = Xβ

**Negative Binomial:**
- **Overdispersion**: When Var[Y] > E[Y]
- **Gamma Heterogeneity**: Additional parameter for variance
- **Likelihood Ratio Test**: Test Poisson vs. Negative Binomial

## Non-Linear Regression Models

### Threshold Models

**Regime-Switching Models:**
- **Structural Breaks**: Different regimes with different parameters
- **Threshold Variable**: Determines regime switching
- **Financial Applications**: Bull/bear markets, high/low volatility periods

**Threshold Autoregression (TAR):**
yt = α1 + β1yt-1 + εt if yt-1 ≤ γ
yt = α2 + β2yt-1 + εt if yt-1 > γ

**Smooth Transition Models:**
- **Logistic Smooth Transition**: Gradual regime changes
- **Exponential Smooth Transition**: Alternative transition function
- **Linearity Testing**: Tests for non-linear dynamics

### Polynomial and Spline Models

**Polynomial Regression:**
- **Higher Order Terms**: X, X², X³, etc.
- **Overfitting Risk**: High order polynomials may overfit
- **Turning Points**: Critical for identifying extrema

**Spline Functions:**
- **Piecewise Polynomials**: Different functions in different regions
- **Knot Points**: Locations where function changes
- **Smoothness Constraints**: Continuity at knot points
- **B-Splines**: Basis functions for flexible functional forms

## Model Selection and Validation

### Information Criteria

**Akaike Information Criterion (AIC):**
AIC = -2ln(L) + 2k

**Bayesian Information Criterion (BIC):**
BIC = -2ln(L) + k·ln(n)

**Model Selection:**
- **Lower Values Better**: Choose model with lowest IC
- **BIC Penalty**: Stronger penalty for additional parameters
- **AIC vs BIC**: AIC favors more complex models
- **Cross-Validation**: Alternative approach using out-of-sample fit

### Regularization Methods

**Ridge Regression:**
- **L2 Penalty**: Σβ²j penalty term
- **Shrinkage**: Coefficients shrunk toward zero
- **Multicollinearity**: Handles correlated predictors well
- **Tuning Parameter**: λ chosen via cross-validation

**LASSO (L1 Regularization):**
- **L1 Penalty**: Σ|βj| penalty term
- **Variable Selection**: Can set coefficients exactly to zero
- **Sparse Solutions**: Automatically selects relevant variables
- **Elastic Net**: Combines L1 and L2 penalties

**Applications in Finance:**
- **Factor Model Selection**: Choose relevant risk factors
- **Portfolio Construction**: Select assets for inclusion
- **Forecasting Models**: Variable selection for prediction
- **High-Dimensional Data**: Many predictors, fewer observations

## Time-Varying Parameter Models

### Kalman Filter Applications

**State Space Models:**
- **Observation Equation**: Yt = Ztαt + εt
- **State Equation**: αt+1 = Ttαt + Rtηt
- **Time-Varying Parameters**: Parameters evolve according to state equation

**Financial Applications:**
- **Beta Estimation**: Time-varying market betas
- **Volatility Modeling**: Unobserved volatility as state variable
- **Yield Curve Models**: Dynamic term structure modeling
- **Portfolio Optimization**: Time-varying expected returns

### Markov-Switching Models

**Regime Switching:**
- **Discrete States**: Parameters switch between discrete regimes
- **Transition Probabilities**: Markov chain governing regime changes
- **Filtering**: Inference about current regime
- **Smoothing**: Inference about historical regimes

**Hamilton Model:**
yt = α(st) + β(st)yt-1 + σ(st)εt

Where st follows Markov chain with transition matrix P

Advanced econometric techniques provide powerful tools for addressing complex relationships and data structures commonly encountered in financial analysis and modeling.`
        }
      ],
      quiz: {
        id: "econometrics-quiz",
        title: "Econometric Methods Quiz",
        description: "Test your understanding of regression analysis and model validation",
        timeLimit: 25,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "When should you use fixed effects instead of random effects in panel data?",
            options: [
              "When you have a large number of time periods",
              "When individual effects are correlated with explanatory variables",
              "When you want to make inferences about the population",
              "When you have missing data"
            ],
            correctAnswer: 1,
            explanation: "Fixed effects should be used when individual effects are correlated with explanatory variables, as random effects would produce biased estimates in this case."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the main purpose of using instrumental variables?",
            options: [
              "To increase the sample size",
              "To address endogeneity problems",
              "To improve model fit (R-squared)",
              "To reduce standard errors"
            ],
            correctAnswer: 1,
            explanation: "Instrumental variables are used to address endogeneity problems when explanatory variables are correlated with the error term, providing consistent estimates."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "In LASSO regression, what happens to less important variables?",
            options: [
              "They get larger coefficients",
              "They get coefficients shrunk toward but not to zero",
              "They can have coefficients set exactly to zero",
              "They are automatically transformed"
            ],
            correctAnswer: 2,
            explanation: "LASSO regression can set coefficients exactly to zero due to the L1 penalty, effectively performing automatic variable selection."
          }
        ]
      }
    },

    {
      id: "time-series",
      title: "Time Series Analysis",
      description: "ARIMA models, volatility modeling, and forecasting techniques",
      order: 3,
      estimatedHours: 5.0,
      content: [
        {
          id: "arima-models",
          title: "ARIMA and Volatility Models",
          description: "Time series modeling for financial data",
          type: "video",
          duration: 50
        },
        {
          id: "time-series-fundamentals",
          title: "Time Series Analysis Fundamentals",
          description: "Stationarity, autocorrelation, and unit root testing",
          type: "text",
          duration: 55,
          textContent: `# Time Series Analysis in Finance

## Stationarity and Unit Roots

### Stationarity Concepts

**Strict Stationarity:**
- **Definition**: Joint distribution unchanged by time shifts
- **Mathematical**: F(xt+1,...,xt+k) = F(xs+1,...,xs+k) for all t,s,k
- **Too Restrictive**: Rarely satisfied in practice
- **Alternative**: Focus on weak stationarity

**Weak (Covariance) Stationarity:**
- **Constant Mean**: E[Xt] = μ for all t
- **Constant Variance**: Var[Xt] = σ² for all t  
- **Time-Invariant Autocovariance**: Cov[Xt, Xt+h] depends only on h
- **Financial Relevance**: Most financial time series are non-stationary

**Non-Stationarity in Finance:**
- **Stock Prices**: Exhibit trends, non-constant variance
- **Exchange Rates**: Regime changes, structural breaks
- **Interest Rates**: Time-varying levels and volatility
- **Economic Indicators**: Growth trends, cyclical patterns

### Unit Root Testing

**Augmented Dickey-Fuller (ADF) Test:**

**Test Regression:**
ΔYt = α + βt + γYt-1 + ΣδiΔYt-i + εt

**Hypotheses:**
- H0: γ = 0 (unit root, non-stationary)
- H1: γ < 0 (stationary)

**Critical Values:**
- Non-standard distribution (not t-distribution)
- MacKinnon critical values depend on sample size
- Different models: no constant, constant, trend

**Phillips-Perron (PP) Test:**
- **Non-Parametric**: Doesn't assume specific lag structure
- **Heteroskedasticity Robust**: Uses Newey-West correction
- **Autocorrelation Robust**: Adjusts for serial correlation
- **Comparison with ADF**: Often gives similar results

**KPSS Test:**
- **Null Hypothesis**: Series is stationary
- **Alternative**: Unit root non-stationarity
- **Complementary**: Use with ADF for confirmation
- **Test Statistic**: Based on partial sum process

### Differencing and Integration

**Order of Integration:**
- **I(0)**: Stationary series
- **I(1)**: First difference stationary (most common)
- **I(2)**: Second difference stationary (rare in finance)
- **Fractional Integration**: d can be non-integer

**Over-Differencing:**
- **Problem**: Differencing stationary series introduces MA component
- **Consequences**: Reduced forecasting accuracy
- **Solution**: Careful testing before differencing

## Autoregressive Moving Average (ARMA) Models

### Autoregressive (AR) Models

**AR(p) Model:**
Xt = c + φ1Xt-1 + φ2Xt-2 + ... + φpXt-p + εt

**Stationarity Conditions:**
- **AR(1)**: |φ1| < 1
- **AR(2)**: φ1 + φ2 < 1, φ2 - φ1 < 1, |φ2| < 1
- **General**: All roots of characteristic equation outside unit circle

**Autocorrelation Function (ACF):**
- **Geometric Decay**: AR processes show exponential decay
- **Identification**: ACF pattern suggests AR order
- **Sample ACF**: Computed from data for model identification

**Partial Autocorrelation Function (PACF):**
- **Definition**: Correlation controlling for intermediate lags
- **AR Identification**: PACF cuts off after lag p for AR(p)
- **Yule-Walker Equations**: Theoretical relationship

### Moving Average (MA) Models

**MA(q) Model:**
Xt = μ + εt + θ1εt-1 + θ2εt-2 + ... + θqεt-q

**Invertibility Conditions:**
- **MA(1)**: |θ1| < 1
- **General**: All roots of characteristic equation outside unit circle
- **Importance**: Ensures unique representation

**ACF Properties:**
- **Cut-off**: ACF equals zero after lag q
- **PACF**: Geometric decay for MA processes
- **Identification**: Use ACF pattern to determine q

### ARMA(p,q) Models

**General Form:**
φ(L)Xt = θ(L)εt

Where:
- φ(L) = 1 - φ1L - ... - φpL^p (AR polynomial)
- θ(L) = 1 + θ1L + ... + θqL^q (MA polynomial)
- L = lag operator

**Model Identification:**
- **ACF and PACF Patterns**: Both tail off for ARMA
- **Information Criteria**: AIC, BIC for model selection
- **Parsimony Principle**: Prefer simpler models

## ARIMA Models

### Integrated ARIMA(p,d,q)

**Difference Operator:**
Δ = 1 - L (first difference)
Δ⁷ = (1-L)^d (d-th difference)

**ARIMA Model:**
φ(L)(1-L)^d Xt = θ(L)εt

**Common Models:**
- **ARIMA(0,1,0)**: Random walk
- **ARIMA(0,1,1)**: Random walk with MA error
- **ARIMA(1,1,0)**: Random walk with AR component

### Seasonal ARIMA Models

**SARIMA(p,d,q)(P,D,Q)s:**
φ(L)Φ(L^s)(1-L)^d(1-L^s)^D Xt = θ(L)Θ(L^s)εt

Where:
- s = seasonal period (12 for monthly, 4 for quarterly)
- (P,D,Q) = seasonal AR, differencing, MA orders
- Φ, Θ = seasonal AR and MA polynomials

**Financial Applications:**
- **Quarterly Earnings**: Seasonal patterns in corporate earnings
- **Economic Indicators**: Seasonal adjustment importance
- **Commodity Prices**: Agricultural seasonal patterns

## Volatility Modeling

### ARCH Models

**Autoregressive Conditional Heteroskedasticity:**

**Mean Equation:**
rt = μ + εt

**Variance Equation:**
σt² = α₀ + α₁εt-1² + ... + αqεt-q²

**ARCH(1) Properties:**
- **Conditional Variance**: Time-varying based on past shocks
- **Unconditional Variance**: Constant if α₁ < 1
- **Fat Tails**: Generates excess kurtosis
- **Volatility Clustering**: High volatility followed by high volatility

### GARCH Models

**Generalized ARCH:**
σt² = α₀ + α₁εt-1² + β₁σt-1²

**GARCH(1,1) Properties:**
- **Persistence**: α₁ + β₁ measures volatility persistence
- **Stationarity**: α₁ + β₁ < 1 required
- **Long Memory**: High persistence common in financial data

**Extensions:**

**EGARCH (Nelson):**
- **Asymmetric Effects**: Negative shocks have larger impact
- **Leverage Effect**: Bad news increases volatility more
- **Log Specification**: Ensures positive variance

**GJR-GARCH (Glosten-Jagannathan-Runkle):**
σt² = α₀ + α₁εt-1² + γI[t-1]εt-1² + β₁σt-1²

Where I[t-1] = 1 if εt-1 < 0 (negative shock indicator)

## Vector Autoregression (VAR)

### Multivariate Time Series

**VAR(p) Model:**
Yt = A₁Yt-1 + A₂Yt-2 + ... + ApYt-p + εt

Where:
- Yt = (n × 1) vector of variables
- Ai = (n × n) coefficient matrices
- εt = (n × 1) error vector

**Estimation:**
- **Equation-by-Equation OLS**: Each equation estimated separately
- **Seemingly Unrelated Regression**: Efficiency gains with correlation
- **Maximum Likelihood**: Full system estimation

### Granger Causality

**Concept:**
- **Granger Causality**: X Granger-causes Y if past X improves Y forecasts
- **Not True Causality**: Statistical relationship, not causal
- **Bidirectional**: Y can also Granger-cause X

**Testing:**
- **F-Test**: Test significance of lagged X in Y equation
- **Joint Test**: Test all lags of X simultaneously
- **Instantaneous Causality**: Contemporaneous correlation

### Impulse Response Functions

**Structural Shocks:**
- **Identification Problem**: Need to recover structural shocks
- **Cholesky Decomposition**: Recursive identification
- **Structural VAR**: Theory-based restrictions

**IRF Interpretation:**
- **Dynamic Effects**: Response of variables to shocks over time
- **Confidence Bands**: Bootstrap standard errors
- **Persistence**: How long effects last

### Cointegration and Vector Error Correction

**Cointegration Concept:**
- **Long-Run Relationship**: Linear combination is stationary
- **Error Correction**: Short-run deviations correct toward equilibrium
- **Economic Meaning**: Long-run economic relationships

**Engle-Granger Method:**
1. Test variables for unit roots
2. Estimate long-run relationship
3. Test residuals for stationarity
4. Estimate error correction model if cointegrated

**Johansen Method:**
- **System Approach**: Tests for multiple cointegrating relationships
- **Trace Test**: Tests for number of cointegrating vectors
- **Maximum Eigenvalue Test**: Sequential testing
- **VECM**: Vector Error Correction Model specification

**Financial Applications:**
- **Term Structure**: Cointegration among interest rates
- **Exchange Rates**: Purchasing power parity relationships
- **Stock Markets**: Index arbitrage relationships
- **Commodity Markets**: Spot-futures relationships

Time series analysis provides essential tools for understanding and modeling the dynamic behavior of financial markets and economic variables.`
        },
        {
          id: "volatility-modeling-interactive",
          title: "GARCH Volatility Modeling Tool",
          description: "Build and estimate GARCH models for financial volatility",
          type: "finance-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "volatility-modeling",
            instructions: "Estimate different GARCH models for stock return volatility. Compare model performance and analyze volatility clustering patterns.",
            scenario: {
              assetReturns: "S&P 500 Daily Returns",
              samplePeriod: "5 years",
              models: ["ARCH(1)", "GARCH(1,1)", "EGARCH(1,1)", "GJR-GARCH(1,1)"],
              evaluationMetrics: ["Log-Likelihood", "AIC", "BIC", "Ljung-Box Test"]
            },
            hints: [
              "Check for ARCH effects before fitting GARCH models",
              "GARCH(1,1) is often sufficient for daily returns",
              "Asymmetric models capture leverage effects",
              "Validate models with standardized residuals tests"
            ]
          }
        }
      ],
      quiz: {
        id: "time-series-quiz",
        title: "Time Series Analysis Quiz",
        description: "Test your understanding of time series modeling techniques",
        timeLimit: 25,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main characteristic of a unit root process?",
            options: [
              "It has constant mean and variance",
              "Shocks have permanent effects",
              "It displays mean reversion",
              "It has no autocorrelation"
            ],
            correctAnswer: 1,
            explanation: "A unit root process is non-stationary and shocks have permanent effects, meaning the series doesn't revert to a fixed mean."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In a GARCH(1,1) model, what does high persistence (α + β close to 1) indicate?",
            options: [
              "Volatility shocks die out quickly",
              "The model is misspecified",
              "Volatility shocks persist for a long time",
              "The series is non-stationary"
            ],
            correctAnswer: 2,
            explanation: "High persistence in GARCH models means volatility shocks take a long time to dissipate, which is commonly observed in financial markets."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What does cointegration between two I(1) variables imply?",
            options: [
              "The variables are stationary",
              "There exists a long-run equilibrium relationship",
              "The variables are independent",
              "One variable causes the other"
            ],
            correctAnswer: 1,
            explanation: "Cointegration means there exists a linear combination of the I(1) variables that is I(0), indicating a stable long-run relationship."
          }
        ]
      }
    },

    {
      id: "monte-carlo",
      title: "Monte Carlo Methods",
      description: "Simulation techniques for risk analysis and option pricing",
      order: 4,
      estimatedHours: 4.5,
      content: [
        {
          id: "simulation-basics",
          title: "Monte Carlo Simulation Fundamentals",
          description: "Random number generation and simulation techniques",
          type: "video",
          duration: 40
        },
        {
          id: "monte-carlo-theory",
          title: "Monte Carlo Methods Theory and Applications",
          description: "Theoretical foundation and financial applications of simulation methods",
          type: "text",
          duration: 60,
          textContent: `# Monte Carlo Methods in Finance

## Theoretical Foundation

### Random Number Generation

**Pseudorandom Numbers:**
- **Linear Congruential Generator**: Xn+1 = (aXn + c) mod m
- **Mersenne Twister**: High-quality generator with long period
- **Cryptographically Secure**: For security-sensitive applications
- **Quality Testing**: Statistical tests for randomness

**Uniform to Other Distributions:**

**Inverse Transform Method:**
1. Generate U ~ Uniform(0,1)
2. Compute X = F^(-1)(U) where F^(-1) is inverse CDF
3. X follows distribution F

**Examples:**
- **Exponential**: X = -λ ln(1-U)
- **Weibull**: X = β[-ln(1-U)]^(1/α)
- **Pareto**: X = xm(1-U)^(-1/α)

**Box-Muller Transform (Normal):**
- **Method**: Transform two uniform variables to two normal
- **Formula**: 
  - Z1 = √(-2ln(U1))cos(2πU2)
  - Z2 = √(-2ln(U1))sin(2πU2)
- **Efficiency**: Generates two normal variates per iteration

**Acceptance-Rejection Method:**
1. Find envelope function g(x) such that f(x) ≤ cg(x)
2. Generate X from g and U from uniform
3. Accept X if U ≤ f(X)/(cg(X))
4. Useful for complex distributions without closed-form inverse

### Law of Large Numbers and CLT

**Strong Law of Large Numbers:**
θ̂n = (1/n)ΣXi → E[X] almost surely as n → ∞

**Monte Carlo Estimator:**
- **Unbiased**: E[θ̂n] = θ
- **Consistent**: θ̂n →p θ
- **Standard Error**: SE[θ̂n] = σ/√n

**Central Limit Theorem:**
√n(θ̂n - θ) →d N(0, σ²)

**Confidence Intervals:**
θ̂n ± zα/2 × SE[θ̂n]

## Variance Reduction Techniques

### Antithetic Variates

**Concept:**
- Generate U and 1-U pairs
- Use both for estimation
- Reduces variance if payoffs negatively correlated

**Implementation:**
For each uniform U, generate:
- X1 = F^(-1)(U)
- X2 = F^(-1)(1-U)

**Variance Reduction:**
Var[(Y1 + Y2)/2] = [Var[Y1] + Var[Y2] + 2Cov[Y1,Y2]]/4

Reduction occurs when Cov[Y1,Y2] < 0

### Control Variates

**Method:**
θ̂cv = θ̂ + c(Ẑ - E[Z])

Where:
- Z = control variate with known expectation E[Z]
- c = control coefficient

**Optimal Control Coefficient:**
c* = -Cov[θ̂, Z]/Var[Z]

**Variance Reduction:**
Var[θ̂cv] = Var[θ̂](1 - ρ²)

Where ρ is correlation between θ̂ and Z

### Importance Sampling

**Concept:**
- Change probability measure to focus on important regions
- Particularly useful for rare events
- Weight samples by likelihood ratios

**Estimator:**
θ̂IS = (1/n)Σ[h(Xi)f(Xi)/g(Xi)]

Where:
- f(x) = original density
- g(x) = importance sampling density
- Xi ~ g

**Optimal Importance Density:**
g*(x) = |h(x)|f(x)/∫|h(x)|f(x)dx

### Stratified Sampling

**Method:**
1. Partition sample space into strata
2. Sample from each stratum
3. Combine estimates with appropriate weights

**Proportional Allocation:**
ni = n × pi

Where pi = probability of stratum i

**Optimal Allocation:**
ni = n × (piσi)/Σ(pjσj)

Where σi = standard deviation in stratum i

## Financial Applications

### Option Pricing

**European Options:**

**Black-Scholes Simulation:**
1. Generate stock price paths: ST = S0 exp((r-σ²/2)T + σ√T × Z)
2. Calculate payoffs: max(ST-K, 0) for calls
3. Discount to present value: e^(-rT) × payoff
4. Average across simulations

**Path-Dependent Options:**

**Asian Options:**
- **Arithmetic Average**: (1/n)ΣSi
- **Geometric Average**: (ΠSi)^(1/n)
- **Discrete vs. Continuous**: Monitoring frequency effects

**Barrier Options:**
- **Knock-Out**: Option expires if barrier hit
- **Knock-In**: Option activated if barrier hit
- **Monitoring**: Continuous vs. discrete monitoring

**American Options:**
- **Least Squares Monte Carlo**: Longstaff-Schwartz method
- **Regression**: Estimate continuation value
- **Optimal Exercise**: Exercise if immediate payoff > continuation value

### Risk Management Applications

**Value at Risk (VaR):**

**Historical Simulation:**
1. Use historical return data
2. Apply to current portfolio
3. Sort simulated P&L
4. VaR = percentile of loss distribution

**Monte Carlo VaR:**
1. Model risk factors (returns, rates, volatilities)
2. Generate scenarios for risk factors
3. Revalue portfolio under each scenario
4. Calculate VaR from simulated P&L distribution

**Expected Shortfall (ES):**
ESα = E[Loss | Loss > VaRα]

**Stress Testing:**
- **Scenario Analysis**: Specific adverse scenarios
- **Sensitivity Analysis**: Impact of factor changes
- **Reverse Stress Testing**: Scenarios causing specific losses

### Portfolio Optimization

**Return Simulation:**

**Multivariate Normal:**
1. Estimate mean vector μ and covariance matrix Σ
2. Generate: r = μ + L × Z where L is Cholesky factor of Σ
3. Use for portfolio optimization under uncertainty

**Bootstrap Methods:**
1. Resample historical returns with replacement
2. Maintain empirical return distribution
3. Avoid distributional assumptions
4. Account for higher moments and tail behavior

**Resampled Efficient Frontier:**
1. Generate multiple return scenarios
2. Optimize portfolio for each scenario
3. Average optimal weights across scenarios
4. Reduces estimation error in optimization

## Advanced Simulation Techniques

### Quasi-Monte Carlo

**Low-Discrepancy Sequences:**
- **Halton Sequence**: Based on prime numbers
- **Sobol Sequence**: Binary sequences with good uniformity
- **Faure Sequence**: Base-p sequences

**Advantages:**
- **Better Convergence**: O(1/n) vs. O(1/√n) for MC
- **Deterministic**: Reproducible results
- **High Dimensions**: Effective in moderate dimensions

**Limitations:**
- **Curse of Dimensionality**: Performance degrades in very high dimensions
- **Randomization**: May need randomized QMC for confidence intervals

### Stochastic Differential Equations

**Euler-Maruyama Scheme:**
Xt+Δt = Xt + μ(Xt, t)Δt + σ(Xt, t)ΔW

Where ΔW ~ N(0, Δt)

**Milstein Scheme:**
Xt+Δt = Xt + μΔt + σΔW + (1/2)σσ'[(ΔW)² - Δt]

**Higher-Order Accuracy**: Better convergence properties

**Applications:**
- **Heston Model**: Stochastic volatility
- **Vasicek Model**: Interest rate modeling
- **Jump-Diffusion**: Merton model with jumps

### Markov Chain Monte Carlo (MCMC)

**Metropolis-Hastings Algorithm:**
1. Propose new state x' from current state x
2. Calculate acceptance probability: α = min(1, π(x')q(x|x')/[π(x)q(x'|x)])
3. Accept with probability α
4. Used for Bayesian inference in finance

**Gibbs Sampling:**
- **Conditional Sampling**: Sample from full conditional distributions
- **Block Updates**: Update parameters in groups
- **Applications**: Factor models, regime-switching models

## Implementation and Computational Aspects

### Parallel Computing

**Embarrassingly Parallel:**
- Monte Carlo simulations are naturally parallel
- Split simulations across processors/cores
- Minimal communication between processes

**GPU Computing:**
- **CUDA/OpenCL**: Parallel processing on graphics cards
- **Thousands of Threads**: Suitable for MC simulations
- **Memory Considerations**: Efficient data transfer

### Software Implementation

**Python Libraries:**
- **NumPy**: Array operations and random number generation
- **SciPy**: Statistical distributions and optimization
- **QuantLib**: Comprehensive quantitative finance library

**R Packages:**
- **RQuantLib**: R interface to QuantLib
- **fOptions**: Options pricing and Greeks
- **RiskPortfolios**: Portfolio optimization with MC

**Performance Optimization:**
- **Vectorization**: Avoid loops when possible
- **Memory Management**: Efficient array operations
- **Profiling**: Identify computational bottlenecks

### Validation and Testing

**Convergence Testing:**
- **Plot Estimates vs. Sample Size**: Visual convergence check
- **Confidence Intervals**: Statistical significance of results
- **Multiple Runs**: Check consistency across runs

**Benchmark Testing:**
- **Analytical Solutions**: Compare with closed-form when available
- **Other Methods**: Cross-validate with different approaches
- **Known Results**: Test with problems having known answers

Monte Carlo methods provide powerful and flexible tools for solving complex financial problems that lack closed-form solutions, though careful implementation and validation are essential for reliable results.`
        },
        {
          id: "option-pricing-simulator",
          title: "Monte Carlo Option Pricing Tool",
          description: "Price European and path-dependent options using Monte Carlo simulation",
          type: "finance-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "option-pricing-mc",
            instructions: "Use Monte Carlo simulation to price different types of options. Compare with analytical Black-Scholes prices where available and analyze convergence.",
            scenario: {
              underlyingPrice: 100,
              strikePrice: 105,
              riskFreeRate: 0.05,
              volatility: 0.2,
              timeToMaturity: 0.25,
              optionTypes: ["European Call", "European Put", "Asian Call", "Barrier Call"],
              simulationSizes: [1000, 10000, 100000, 1000000]
            },
            hints: [
              "Start with small sample sizes to see convergence pattern",
              "Compare European option prices with Black-Scholes formula",
              "Use antithetic variates to reduce variance",
              "Monitor standard error as sample size increases"
            ]
          }
        }
      ],
      quiz: {
        id: "monte-carlo-quiz",
        title: "Monte Carlo Methods Quiz",
        description: "Test your understanding of simulation techniques and applications",
        timeLimit: 25,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main advantage of antithetic variates in Monte Carlo simulation?",
            options: [
              "Increases the sample size",
              "Reduces computational time",
              "Reduces variance of estimates",
              "Eliminates bias in estimates"
            ],
            correctAnswer: 2,
            explanation: "Antithetic variates reduce the variance of Monte Carlo estimates by using negatively correlated pairs of random variables, leading to more precise estimates."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In Monte Carlo VaR estimation, what does increasing the number of simulations primarily achieve?",
            options: [
              "Changes the VaR estimate",
              "Reduces the standard error of the VaR estimate",
              "Eliminates model risk",
              "Increases the confidence level"
            ],
            correctAnswer: 1,
            explanation: "Increasing simulations reduces the standard error (uncertainty) of the VaR estimate, making it more precise, but doesn't change the expected value or eliminate model risk."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Which Monte Carlo variance reduction technique is most effective for rare event simulation?",
            options: [
              "Antithetic variates",
              "Control variates",
              "Importance sampling",
              "Stratified sampling"
            ],
            correctAnswer: 2,
            explanation: "Importance sampling is most effective for rare events because it changes the sampling distribution to focus on the rare event region, dramatically improving efficiency."
          }
        ]
      }
    },

    {
      id: "optimization",
      title: "Optimization Techniques",
      description: "Portfolio optimization, linear programming, and optimization algorithms",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "portfolio-optimization",
          title: "Portfolio Optimization Methods",
          description: "Mean-variance optimization and advanced techniques",
          type: "video",
          duration: 45
        },
        {
          id: "optimization-theory",
          title: "Mathematical Optimization in Finance",
          description: "Linear, quadratic, and nonlinear optimization techniques for financial problems",
          type: "text",
          duration: 55,
          textContent: `# Mathematical Optimization in Finance

## Linear Programming

### Standard Form

**Primal Problem:**
min c^T x
subject to:
Ax = b
x ≥ 0

Where:
- c = objective function coefficients
- A = constraint coefficient matrix
- b = right-hand side vector
- x = decision variables

**Dual Problem:**
max b^T y
subject to:
A^T y ≤ c

**Strong Duality:**
Optimal primal value = Optimal dual value (when optimal solutions exist)

### Financial Applications

**Asset-Liability Matching:**
- **Objective**: Minimize cost of bond portfolio
- **Constraints**: Match liability cash flows
- **Variables**: Bond holdings (continuous or integer)

**Arbitrage Detection:**
- **Objective**: Maximize profit from arbitrage opportunities
- **Constraints**: Market prices and no-arbitrage conditions
- **Interpretation**: Feasible solution implies arbitrage exists

**Portfolio Optimization with Linear Constraints:**
- **Sector Limits**: Σwi ≤ upper_bound for sector i
- **Position Limits**: |wi| ≤ position_limit
- **Turnover Constraints**: Limit transaction costs

## Quadratic Programming

### Mathematical Formulation

**Standard QP:**
min (1/2)x^T Q x + c^T x
subject to:
Ax = b (equality constraints)
Gx ≤ h (inequality constraints)

**Convexity:**
- **Positive Semidefinite Q**: Convex problem, global optimum
- **Indefinite Q**: Non-convex, local optima possible

**Karush-Kuhn-Tucker (KKT) Conditions:**
1. **Stationarity**: ∇f + λ^T∇g + μ^T∇h = 0
2. **Primal Feasibility**: g(x) = 0, h(x) ≤ 0
3. **Dual Feasibility**: μ ≥ 0
4. **Complementary Slackness**: μi hi(x) = 0

### Mean-Variance Optimization

**Markowitz Problem:**
min (1/2)w^T Σ w
subject to:
μ^T w = μ_target (return constraint)
1^T w = 1 (budget constraint)
w ≥ 0 (no short sales)

**Lagrangian:**
L = (1/2)w^T Σ w - λ1(μ^T w - μ_target) - λ2(1^T w - 1) - μ^T w

**First-Order Conditions:**
Σ w - λ1 μ - λ2 1 - μ = 0

**Solution Methods:**
- **Critical Line Algorithm**: Exploits QP structure
- **Interior Point Methods**: Handle large-scale problems
- **Active Set Methods**: Sequential constraint handling

### Risk Budgeting

**Equal Risk Contribution:**
- **Risk Contribution**: RCi = wi × (∂σ/∂wi)
- **Constraint**: RCi = σ/n for all i
- **Non-Linear**: Requires iterative solution methods

**Risk Parity Portfolio:**
wi × (Σw)i = (1/n) × w^T Σ w

**Solution Approaches:**
- **Cyclical Coordinate Descent**: Update one weight at a time
- **Newton-Raphson**: Second-order optimization
- **Convex Optimization**: Reformulate as convex problem

## Nonlinear Programming

### Unconstrained Optimization

**Gradient Descent:**
xk+1 = xk - αk ∇f(xk)

Where αk is step size (learning rate)

**Newton's Method:**
xk+1 = xk - [∇²f(xk)]^(-1) ∇f(xk)

**Quasi-Newton Methods:**
- **BFGS**: Approximates Hessian using gradient information
- **L-BFGS**: Limited memory version for large-scale problems
- **Update Formula**: Hk+1 = Hk + correction terms

### Constrained Nonlinear Programming

**Sequential Quadratic Programming (SQP):**
1. Solve QP subproblem at current point
2. Use QP solution as search direction
3. Perform line search to determine step size
4. Update approximation of Lagrangian Hessian

**Interior Point Methods:**
- **Barrier Functions**: Replace inequality constraints with penalty terms
- **Central Path**: Follow path of constrained optima as barrier parameter → 0
- **Primal-Dual**: Simultaneously solve primal and dual problems

**Augmented Lagrangian:**
L(x, λ, ρ) = f(x) + λ^T g(x) + (ρ/2)||g(x)||^2

## Stochastic Programming

### Two-Stage Stochastic Programming

**Mathematical Model:**
min c^T x + E[ξ Q(x, ξ)]
subject to:
Ax = b
x ≥ 0

Where:
- x = first-stage (here-and-now) decisions
- Q(x, ξ) = optimal value of second-stage problem
- ξ = random parameters

**Second-Stage Problem:**
Q(x, ξ) = min q^T y
subject to:
Wy = h - Tx
y ≥ 0

**L-Shaped Algorithm:**
1. **Master Problem**: Approximate expected second-stage cost
2. **Subproblems**: Evaluate second-stage costs for scenarios
3. **Cuts**: Add optimality and feasibility cuts to master
4. **Iterate**: Until convergence

### Portfolio Optimization Under Uncertainty

**Scenario-Based Approach:**
min Σs ps [(1/2)ws^T Σs ws]
subject to:
Σs ps [μ_s^T ws] ≥ μ_target
Σs ps ws = w

Where:
- s indexes scenarios
- ps = probability of scenario s
- ws = portfolio weights in scenario s

**Robust Optimization:**
min maxξ∈Ξ f(x, ξ)
subject to:
g(x, ξ) ≤ 0 for all ξ ∈ Ξ

**Applications:**
- **Parameter Uncertainty**: Robust mean-variance optimization
- **Worst-Case Analysis**: Minimize maximum loss
- **Uncertainty Sets**: Ellipsoidal, box, polyhedral uncertainty

## Integer and Mixed-Integer Programming

### Binary Variables in Finance

**Asset Selection:**
min w^T Σ w
subject to:
μ^T w ≥ μ_target
Σ wi = 1
xi wi ≤ wi ≤ xi wi_max
xi ∈ {0, 1}

Where xi = 1 if asset i is selected

**Transaction Costs:**
- **Fixed Costs**: Binary variables for trading decisions
- **Piecewise Linear**: Different cost rates for different trade sizes
- **Minimum Trade Size**: Logical constraints with binary variables

### Branch and Bound

**Algorithm:**
1. **Relaxation**: Solve LP relaxation of MILP
2. **Branching**: Create subproblems by fixing integer variables
3. **Bounding**: Use LP bounds to prune subproblems
4. **Selection**: Choose next subproblem to explore

**Applications:**
- **Portfolio Cardinality**: Limit number of assets
- **Rebalancing**: Minimize number of trades
- **Hedging**: Select optimal hedging instruments

## Multi-Objective Optimization

### Pareto Efficiency

**Pareto Optimal Solutions:**
- **Definition**: No other feasible solution dominates
- **Efficient Frontier**: Set of all Pareto optimal solutions
- **Trade-offs**: Different objective weights yield different solutions

**Scalarization Methods:**

**Weighted Sum:**
min λ1 f1(x) + λ2 f2(x) + ... + λk fk(x)

**ε-Constraint Method:**
min f1(x)
subject to:
fi(x) ≤ εi for i = 2, ..., k

### Financial Applications

**Risk-Return-Liquidity:**
- **Objective 1**: Maximize expected return
- **Objective 2**: Minimize portfolio risk
- **Objective 3**: Maximize portfolio liquidity

**ESG Integration:**
- **Financial Objectives**: Return, risk, cost
- **ESG Objectives**: Environmental impact, social responsibility, governance
- **Weighting**: Investor preferences for ESG vs. financial performance

## Algorithmic Trading Optimization

### Execution Optimization

**Optimal Execution (Almgren-Chriss):**
min E[Cost] + λ Var[Cost]

Where cost includes:
- **Market Impact**: Temporary and permanent price impact
- **Timing Risk**: Volatility of price during execution
- **Implementation Shortfall**: Total cost vs. decision price

**TWAP (Time-Weighted Average Price):**
- **Objective**: Minimize deviation from TWAP benchmark
- **Constraint**: Complete execution within time horizon
- **Solution**: Uniform execution rate (under assumptions)

**VWAP (Volume-Weighted Average Price):**
- **Objective**: Minimize deviation from VWAP benchmark
- **Strategy**: Follow historical volume patterns
- **Extensions**: Adapt to real-time volume information

### Portfolio Rebalancing

**Transaction Cost Models:**
- **Linear**: Proportional to trade size
- **Square-Root**: Market impact proportional to √(trade size)
- **Fixed + Variable**: Setup cost plus proportional cost

**Optimization Problem:**
min (w - w_target)^T Σ (w - w_target) + λ × transaction_costs

subject to:
Budget and position constraints

## Implementation and Computational Aspects

### Software Tools

**Commercial Solvers:**
- **CPLEX**: Linear, quadratic, mixed-integer programming
- **Gurobi**: High-performance optimization solver
- **Mosek**: Specialized in conic optimization

**Open Source:**
- **CVXPY (Python)**: Domain-specific language for convex optimization
- **JuMP (Julia)**: Mathematical programming language
- **COIN-OR**: Open-source optimization software

**Financial Libraries:**
- **QuantLib**: Comprehensive quantitative finance library
- **PyPortfolioOpt**: Python portfolio optimization
- **CVXR (R)**: Convex optimization in R

### Performance Considerations

**Problem Scaling:**
- **Large Universes**: Thousands of assets
- **High-Frequency**: Real-time optimization requirements
- **Multi-Period**: Dynamic optimization over time

**Approximation Methods:**
- **Sampling**: Reduce number of scenarios
- **Factorization**: Use factor models to reduce dimensionality
- **Warm Starting**: Use previous solution as starting point

**Parallel Computing:**
- **Scenario Decomposition**: Parallel evaluation of scenarios
- **Distributed Optimization**: Split problem across processors
- **GPU Acceleration**: Leverage graphics card computing power

Mathematical optimization provides the foundation for solving complex financial decision problems, from portfolio construction to risk management to algorithmic trading strategies.`
        },
        {
          id: "portfolio-optimizer-tool",
          title: "Portfolio Optimization Laboratory",
          description: "Advanced portfolio optimization with multiple objectives and constraints",
          type: "finance-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "advanced-portfolio-optimization",
            instructions: "Build optimal portfolios using different optimization objectives and constraints. Compare mean-variance, risk parity, and Black-Litterman approaches.",
            scenario: {
              assets: 10,
              optimizationTypes: ["Mean-Variance", "Risk Parity", "Maximum Diversification", "Black-Litterman"],
              constraints: {
                budgetConstraint: true,
                noShortSelling: true,
                sectorLimits: true,
                turnoverLimit: true
              },
              riskModels: ["Sample Covariance", "Factor Model", "Shrinkage Estimator"]
            },
            hints: [
              "Start with simple mean-variance optimization",
              "Risk parity focuses on equal risk contributions",
              "Factor models can improve covariance estimation",
              "Transaction costs affect rebalancing frequency"
            ]
          }
        }
      ],
      quiz: {
        id: "optimization-quiz",
        title: "Optimization Techniques Quiz",
        description: "Test your understanding of mathematical optimization in finance",
        timeLimit: 25,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What type of optimization problem is the classic Markowitz mean-variance portfolio optimization?",
            options: [
              "Linear programming",
              "Quadratic programming",
              "Integer programming",
              "Dynamic programming"
            ],
            correctAnswer: 1,
            explanation: "Markowitz portfolio optimization minimizes a quadratic objective function (portfolio variance) subject to linear constraints, making it a quadratic programming problem."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In risk parity portfolios, what is equalized across all assets?",
            options: [
              "Portfolio weights",
              "Expected returns",
              "Risk contributions",
              "Correlations"
            ],
            correctAnswer: 2,
            explanation: "Risk parity portfolios equalize the risk contributions of all assets, meaning each asset contributes equally to the total portfolio risk."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What is the main advantage of using integer programming in portfolio optimization?",
            options: [
              "Faster computation",
              "Better risk-return trade-off",
              "Ability to model discrete decisions like asset selection",
              "Guaranteed global optimum"
            ],
            correctAnswer: 2,
            explanation: "Integer programming allows modeling of discrete decisions such as whether to include an asset in the portfolio or not, which cannot be handled by continuous optimization."
          }
        ]
      }
    },

    {
      id: "machine-learning",
      title: "Machine Learning in Finance",
      description: "Supervised and unsupervised learning techniques for financial applications",
      order: 6,
      estimatedHours: 5.0,
      content: [
        {
          id: "ml-fundamentals",
          title: "Machine Learning Fundamentals",
          description: "Introduction to machine learning concepts and financial applications",
          type: "video",
          duration: 45
        },
        {
          id: "supervised-learning",
          title: "Supervised Learning for Finance",
          description: "Regression and classification techniques for financial prediction",
          type: "text",
          duration: 60,
          textContent: `# Machine Learning in Finance

## Supervised Learning Fundamentals

### Regression Analysis

**Linear Regression Extensions:**

**Ridge Regression (L2 Regularization):**
- **Objective**: min ||y - Xβ||² + λ||β||²
- **Shrinkage**: Coefficients shrunk toward zero
- **Multicollinearity**: Handles correlated predictors well
- **Bias-Variance Trade-off**: Introduces bias to reduce variance

**LASSO Regression (L1 Regularization):**
- **Objective**: min ||y - Xβ||² + λ||β||₁
- **Sparsity**: Automatic variable selection
- **Feature Selection**: Sets irrelevant coefficients to exactly zero
- **Applications**: Factor selection, stock screening

**Elastic Net:**
- **Combined Penalty**: λ₁||β||₁ + λ₂||β||²
- **Benefits**: Variable selection + handles multicollinearity
- **Parameter Tuning**: Cross-validation for λ₁, λ₂

**Support Vector Regression (SVR):**
- **ε-insensitive Loss**: No penalty for errors within ε-tube
- **Kernel Trick**: Non-linear relationships via kernel functions
- **Robust**: Less sensitive to outliers than OLS
- **Applications**: Volatility forecasting, price prediction

### Tree-Based Methods

**Decision Trees:**
- **Recursive Partitioning**: Split data based on feature values
- **Splitting Criteria**: MSE for regression, Gini/entropy for classification
- **Interpretability**: Easy to understand and visualize
- **Overfitting**: Prone to overfitting without pruning

**Random Forest:**
- **Bootstrap Aggregating**: Average multiple decision trees
- **Feature Randomness**: Random subset of features at each split
- **Out-of-Bag Error**: Built-in cross-validation
- **Variable Importance**: Measure feature contribution

**Gradient Boosting:**
- **Sequential Learning**: Each tree corrects previous mistakes
- **Loss Function**: Flexible loss functions (squared error, absolute error)
- **XGBoost/LightGBM**: Optimized implementations
- **Applications**: Credit scoring, algorithmic trading

### Classification Methods

**Logistic Regression:**
- **Sigmoid Function**: P(y=1) = 1/(1 + e^(-Xβ))
- **Maximum Likelihood**: Non-linear optimization
- **Regularization**: Ridge/LASSO extensions available
- **Interpretability**: Coefficient interpretation as log-odds

**Support Vector Machines:**
- **Maximum Margin**: Find optimal separating hyperplane
- **Kernel Functions**: RBF, polynomial for non-linear boundaries
- **Soft Margin**: Allow misclassification with penalty parameter C
- **Applications**: Credit default prediction, market regime classification

**Naive Bayes:**
- **Bayes' Theorem**: P(y|X) ∝ P(X|y)P(y)
- **Independence Assumption**: Features assumed independent given class
- **Fast Training**: Simple parameter estimation
- **Text Classification**: Effective for document classification

### Ensemble Methods

**Bagging (Bootstrap Aggregating):**
- **Parallel Training**: Multiple models trained independently
- **Variance Reduction**: Averaging reduces overfitting
- **Random Forest**: Most popular bagging method
- **Applications**: Portfolio construction, risk modeling

**Boosting:**
- **Sequential Training**: Models learn from previous errors
- **AdaBoost**: Adaptive boosting with weighted samples
- **Gradient Boosting**: Optimize arbitrary loss functions
- **Bias Reduction**: Focuses on reducing bias

**Stacking:**
- **Meta-Learning**: Train meta-model on base model predictions
- **Cross-Validation**: Prevent information leakage
- **Diversity**: Combine different types of models
- **Applications**: Ensemble trading strategies

## Financial Applications of Machine Learning

### Algorithmic Trading

**Technical Analysis Enhancement:**
- **Pattern Recognition**: Identify chart patterns automatically
- **Feature Engineering**: Create technical indicators
- **Regime Detection**: Classify market conditions
- **Signal Generation**: Combine multiple technical signals

**Fundamental Analysis:**
- **Earnings Prediction**: Forecast company earnings
- **Financial Statement Analysis**: Automated ratio analysis
- **News Sentiment**: Extract sentiment from news articles
- **Factor Models**: Identify risk and return factors

**High-Frequency Trading:**
- **Market Microstructure**: Model order book dynamics
- **Latency Prediction**: Forecast execution delays
- **Optimal Execution**: Dynamic execution strategies
- **Market Making**: Automated bid-ask spread management

### Credit Risk Modeling

**Default Prediction:**
- **Traditional Models**: Logistic regression, linear discriminant analysis
- **ML Models**: Random forest, SVM, neural networks
- **Features**: Financial ratios, market data, alternative data
- **Performance Metrics**: AUC, precision, recall, KS statistic

**Loss Given Default (LGD):**
- **Regression Problem**: Predict recovery rate
- **Features**: Collateral type, seniority, macroeconomic factors
- **Two-Stage Models**: First predict default, then LGD
- **Applications**: Basel II/III regulatory capital

**Exposure at Default (EAD):**
- **Credit Line Utilization**: Predict drawn amount at default
- **Survival Analysis**: Time-to-draw modeling
- **Stress Testing**: EAD under adverse scenarios

### Portfolio Management

**Return Forecasting:**
- **Factor Models**: Machine learning-enhanced factor selection
- **Alternative Data**: Satellite imagery, social media, web scraping
- **Ensemble Forecasting**: Combine multiple prediction models
- **Dynamic Models**: Time-varying return predictions

**Risk Modeling:**
- **Covariance Estimation**: Shrinkage estimators, factor models
- **Volatility Forecasting**: GARCH-ML hybrid models
- **Tail Risk**: Extreme value theory with ML
- **Stress Testing**: Scenario generation using ML

**Asset Allocation:**
- **Regime-Aware Allocation**: Allocate based on market regimes
- **Alternative Data**: Incorporate non-traditional data sources
- **Dynamic Rebalancing**: ML-driven rebalancing signals
- **ESG Integration**: Incorporate ESG scores and sustainability metrics

### Fraud Detection

**Anomaly Detection:**
- **Outlier Detection**: Identify unusual transactions
- **Isolation Forest**: Efficient anomaly detection algorithm
- **One-Class SVM**: Learn normal behavior pattern
- **Autoencoders**: Neural network-based anomaly detection

**Pattern Recognition:**
- **Sequential Patterns**: Detect suspicious transaction sequences
- **Network Analysis**: Identify fraudulent networks
- **Behavioral Modeling**: Learn individual customer patterns
- **Real-Time Detection**: Low-latency fraud detection systems

## Model Validation and Selection

### Cross-Validation Techniques

**Time Series Cross-Validation:**
- **Forward Chaining**: Respect temporal order
- **Expanding Window**: Growing training set
- **Rolling Window**: Fixed-size moving training window
- **Purged Cross-Validation**: Handle overlapping observations

**Blocked Cross-Validation:**
- **Time Gaps**: Prevent information leakage
- **Group-Based**: Respect data structure (e.g., by company)
- **Stratified**: Maintain class balance across folds

### Performance Metrics

**Regression Metrics:**
- **MSE/RMSE**: Standard error metrics
- **MAE**: Robust to outliers
- **MAPE**: Scale-independent percentage error
- **R²**: Explained variance ratio

**Classification Metrics:**
- **Accuracy**: Overall correct classification rate
- **Precision/Recall**: Relevant for imbalanced classes
- **F1-Score**: Harmonic mean of precision and recall
- **AUC-ROC**: Area under receiver operating characteristic curve

**Financial-Specific Metrics:**
- **Information Ratio**: Risk-adjusted return metric
- **Sharpe Ratio**: Return per unit of risk
- **Maximum Drawdown**: Worst peak-to-trough decline
- **Hit Rate**: Percentage of correct directional predictions

### Bias and Overfitting

**Overfitting Detection:**
- **Learning Curves**: Plot training vs. validation performance
- **Regularization**: Add penalty terms to prevent overfitting
- **Early Stopping**: Stop training when validation error increases
- **Model Complexity**: Balance complexity and generalization

**Bias Sources:**
- **Look-Ahead Bias**: Using future information
- **Survivorship Bias**: Excluding failed entities
- **Selection Bias**: Non-random sample selection
- **Data Mining Bias**: Multiple testing without adjustment

## Feature Engineering

### Technical Indicators

**Price-Based Features:**
- **Moving Averages**: SMA, EMA, TEMA
- **Momentum Indicators**: RSI, MACD, Stochastic
- **Volatility Measures**: Bollinger Bands, ATR
- **Volume Indicators**: OBV, Volume Price Trend

**Statistical Features:**
- **Returns**: Simple, log, risk-adjusted returns
- **Volatility**: Rolling standard deviation, GARCH estimates
- **Skewness/Kurtosis**: Higher moment statistics
- **Quantiles**: Percentile-based features

**Cross-Sectional Features:**
- **Relative Strength**: Asset vs. benchmark performance
- **Sector Rankings**: Within-sector performance ranks
- **Factor Loadings**: Exposure to common risk factors
- **Peer Comparisons**: Relative valuation metrics

### Alternative Data

**Textual Data:**
- **News Sentiment**: Natural language processing of news
- **Social Media**: Twitter sentiment, Reddit discussions
- **Earnings Calls**: Transcript analysis and tone
- **SEC Filings**: 10-K, 10-Q automated analysis

**Satellite/Geospatial:**
- **Economic Activity**: Nighttime lights, parking lots
- **Commodity Production**: Agricultural yields, mining activity
- **Retail Activity**: Store traffic, construction activity
- **Environmental Factors**: Weather, climate data

**Alternative Financial Data:**
- **Credit Card Spending**: Consumer spending patterns
- **Web Scraping**: Pricing data, product availability
- **Patent Filings**: Innovation and R&D activity
- **Job Postings**: Employment and business expansion

### Feature Selection

**Filter Methods:**
- **Correlation**: Remove highly correlated features
- **Mutual Information**: Information-theoretic relevance
- **Statistical Tests**: Chi-square, ANOVA F-test
- **Variance Thresholding**: Remove low-variance features

**Wrapper Methods:**
- **Forward Selection**: Incrementally add features
- **Backward Elimination**: Remove features one by one
- **Recursive Feature Elimination**: Iterative feature removal
- **Genetic Algorithms**: Evolutionary feature selection

**Embedded Methods:**
- **LASSO**: L1 regularization for automatic selection
- **Tree-Based Importance**: Feature importance from trees
- **Elastic Net**: Combined L1/L2 regularization
- **Regularized Linear Models**: Built-in feature selection

Machine learning provides powerful tools for extracting insights from financial data, but requires careful validation and understanding of financial market dynamics to be effective.`
        },
        {
          id: "ml-trading-strategy",
          title: "Machine Learning Trading Strategy Builder",
          description: "Build and backtest machine learning-based trading strategies",
          type: "finance-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "ml-strategy-building",
            instructions: "Develop a machine learning trading strategy using different algorithms and features. Compare performance with traditional technical analysis.",
            scenario: {
              algorithms: ["Random Forest", "XGBoost", "SVM", "Logistic Regression"],
              features: {
                technical: ["RSI", "MACD", "Bollinger Bands", "Volume"],
                fundamental: ["P/E Ratio", "ROE", "Debt/Equity"],
                alternative: ["News Sentiment", "Social Media"]
              },
              evaluationPeriod: "2 years",
              rebalancingFrequency: ["Daily", "Weekly", "Monthly"]
            },
            hints: [
              "Start with simple features and algorithms",
              "Use proper cross-validation for time series",
              "Consider transaction costs in strategy evaluation",
              "Regularization helps prevent overfitting"
            ]
          }
        }
      ],
      quiz: {
        id: "machine-learning-quiz",
        title: "Machine Learning in Finance Quiz",
        description: "Test your understanding of ML applications in finance",
        timeLimit: 25,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main advantage of Random Forest over a single decision tree?",
            options: [
              "Faster training time",
              "Better interpretability",
              "Reduced overfitting through ensemble averaging",
              "Handles categorical variables better"
            ],
            correctAnswer: 2,
            explanation: "Random Forest reduces overfitting by averaging predictions from multiple decision trees trained on different bootstrap samples, leading to better generalization."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In financial time series, what type of cross-validation should be used?",
            options: [
              "K-fold cross-validation",
              "Leave-one-out cross-validation",
              "Time series cross-validation (forward chaining)",
              "Stratified cross-validation"
            ],
            correctAnswer: 2,
            explanation: "Time series cross-validation respects the temporal order of data and prevents look-ahead bias by only using past data to predict future values."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What is the primary purpose of regularization in machine learning models?",
            options: [
              "Increase model complexity",
              "Reduce training time",
              "Prevent overfitting",
              "Improve interpretability"
            ],
            correctAnswer: 2,
            explanation: "Regularization adds penalty terms to prevent overfitting by constraining model complexity, leading to better generalization on unseen data."
          }
        ]
      }
    },

    {
      id: "stochastic-processes",
      title: "Stochastic Processes in Finance",
      description: "Random walks, Brownian motion, and stochastic differential equations",
      order: 7,
      estimatedHours: 4.5,
      content: [
        {
          id: "random-processes",
          title: "Random Processes and Brownian Motion",
          description: "Foundation of continuous-time financial modeling",
          type: "video",
          duration: 50
        },
        {
          id: "sde-applications",
          title: "Stochastic Differential Equations in Finance",
          description: "Mathematical framework for modeling asset prices and interest rates",
          type: "text",
          duration: 55,
          textContent: `# Stochastic Processes in Finance

## Brownian Motion and Random Walks

### Mathematical Foundation

**Standard Brownian Motion (Wiener Process):**

A stochastic process {W(t), t ≥ 0} is standard Brownian motion if:
1. **W(0) = 0** (starts at origin)
2. **Independent Increments**: W(t) - W(s) independent of {W(u), u ≤ s}
3. **Normal Increments**: W(t) - W(s) ~ N(0, t-s) for t > s
4. **Continuous Paths**: W(t) is continuous in t

**Properties:**
- **Mean**: E[W(t)] = 0
- **Variance**: Var[W(t)] = t
- **Covariance**: Cov[W(s), W(t)] = min(s,t)
- **Non-Differentiable**: Paths are continuous but nowhere differentiable
- **Quadratic Variation**: [W,W](t) = t

**Geometric Brownian Motion:**
S(t) = S(0) exp[(μ - σ²/2)t + σW(t)]

**Properties:**
- **Log-Normal Distribution**: ln(S(t)/S(0)) ~ N((μ - σ²/2)t, σ²t)
- **Positive Values**: S(t) > 0 for all t
- **Multiplicative Structure**: Relative changes are additive in logs
- **Applications**: Stock prices, exchange rates, commodity prices

### Martingales and Financial Markets

**Martingale Definition:**
A process {M(t)} is a martingale if:
1. **E[|M(t)|] < ∞** (finite expectation)
2. **E[M(t)|F(s)] = M(s)** for t > s (conditional expectation property)

**Examples:**
- **Brownian Motion**: W(t) is a martingale
- **Compensated Poisson**: N(t) - λt is a martingale
- **Discounted Stock Price**: Under risk-neutral measure

**Martingale Representation Theorem:**
Every martingale can be represented as stochastic integral with respect to Brownian motion:
M(t) = M(0) + ∫₀ᵗ φ(s) dW(s)

**Applications:**
- **Option Pricing**: Risk-neutral valuation
- **Hedging**: Delta-hedging strategies
- **Market Efficiency**: Martingale property implies no arbitrage

## Stochastic Differential Equations

### Ito's Formula

**SDE Form:**
dX(t) = μ(X(t),t)dt + σ(X(t),t)dW(t)

**Ito's Lemma:**
For f(X(t),t), we have:
df = (∂f/∂t + μ∂f/∂x + ½σ²∂²f/∂x²)dt + σ∂f/∂x dW(t)

**Key Insight:**
- **Second-Order Term**: ½σ²∂²f/∂x² arises from (dW)² = dt
- **Chain Rule Extension**: Accounts for stochastic nature
- **Applications**: Derivatives pricing, risk management

**Example - Geometric Brownian Motion:**
dS = μS dt + σS dW

Applying Ito's lemma to f(S) = ln(S):
d(ln S) = (μ - σ²/2)dt + σ dW

Integrating:
ln(S(t)/S(0)) = (μ - σ²/2)t + σW(t)

### Mean-Reverting Processes

**Ornstein-Uhlenbeck Process:**
dX(t) = κ(θ - X(t))dt + σ dW(t)

**Properties:**
- **Mean Reversion**: Pull toward long-term mean θ
- **Speed**: κ determines reversion speed
- **Stationary Distribution**: N(θ, σ²/(2κ))
- **Half-Life**: ln(2)/κ

**Solution:**
X(t) = X(0)e^(-κt) + θ(1 - e^(-κt)) + σ∫₀ᵗ e^(-κ(t-s)) dW(s)

**Applications:**
- **Interest Rates**: Vasicek model
- **Volatility**: Mean-reverting volatility models
- **Pairs Trading**: Spread between correlated assets
- **Commodity Prices**: Storage theory applications

**Cox-Ingersoll-Ross (CIR) Process:**
dX(t) = κ(θ - X(t))dt + σ√X(t) dW(t)

**Properties:**
- **Non-Negative**: X(t) ≥ 0 if 2κθ ≥ σ²
- **Square-Root Diffusion**: Volatility proportional to √X
- **Chi-Square Distribution**: Transition density known

## Jump Processes

### Poisson Processes

**Counting Process:**
- **N(t)**: Number of jumps by time t
- **Intensity**: λ (average jumps per unit time)
- **Inter-arrival Times**: Exponentially distributed with rate λ
- **Jump Sizes**: Independent of arrival times

**Compound Poisson Process:**
X(t) = ∑ᵢ₌₁^{N(t)} Yᵢ

Where Yᵢ are i.i.d. jump sizes

**Properties:**
- **E[X(t)] = λtE[Y]**
- **Var[X(t)] = λt(E[Y²])**
- **Characteristic Function**: Known in closed form

### Jump-Diffusion Models

**Merton Jump-Diffusion:**
dS = μS dt + σS dW + S∫ (eˣ - 1) Ñ(dt,dx)

Where:
- Ñ(dt,dx) = compensated Poisson random measure
- x ~ jump size distribution (e.g., Normal)
- μ, σ = drift and diffusion parameters

**Applications:**
- **Stock Price Models**: Capture market crashes
- **Credit Risk**: Default as jump event
- **Interest Rate Models**: Policy rate changes
- **Foreign Exchange**: Central bank interventions

**Kou Double Exponential:**
- **Jump Sizes**: Double exponential distribution
- **Asymmetric**: Different upward/downward jump distributions
- **Tractable**: Semi-analytical option pricing formulas

## Stochastic Volatility Models

### Heston Model

**Stock Price Process:**
dS(t) = rS(t)dt + √v(t)S(t)dW₁(t)

**Volatility Process:**
dv(t) = κ(θ - v(t))dt + σᵥ√v(t)dW₂(t)

**Correlation:**
dW₁(t)dW₂(t) = ρdt

**Parameters:**
- **κ**: Volatility mean reversion speed
- **θ**: Long-term volatility level
- **σᵥ**: Volatility of volatility
- **ρ**: Correlation between price and volatility

**Features:**
- **Stochastic Volatility**: Captures volatility clustering
- **Leverage Effect**: ρ < 0 models negative correlation
- **Semi-Analytical**: Option prices via characteristic functions
- **Calibration**: Market option prices used for parameters

### SABR Model

**Forward Process:**
dF(t) = α(t)F(t)^β dW₁(t)

**Volatility Process:**
dα(t) = να(t) dW₂(t)

**Applications:**
- **Interest Rate Options**: Swaptions, caps, floors
- **FX Options**: Currency option pricing
- **Volatility Surface**: Model entire implied volatility surface
- **Market Practice**: Widely used by practitioners

## Numerical Methods for SDEs

### Euler-Maruyama Scheme

**Discretization:**
Xₙ₊₁ = Xₙ + μ(Xₙ, tₙ)Δt + σ(Xₙ, tₙ)√Δt Zₙ₊₁

Where Zₙ₊₁ ~ N(0,1)

**Properties:**
- **Strong Convergence**: Order 0.5 in Δt
- **Weak Convergence**: Order 1.0 in Δt
- **Simple Implementation**: Easy to code
- **Applications**: Monte Carlo simulation of SDEs

### Milstein Scheme

**Higher-Order Approximation:**
Xₙ₊₁ = Xₙ + μΔt + σ√Δt Zₙ₊₁ + ½σσ'[(√Δt Zₙ₊₁)² - Δt]

**Benefits:**
- **Strong Convergence**: Order 1.0 in Δt
- **Better Accuracy**: Fewer time steps needed
- **Computational Cost**: Requires derivative σ'

### Stochastic Runge-Kutta

**Multi-Stage Methods:**
- **Higher-Order Accuracy**: Better than Euler-Maruyama
- **Computational Complexity**: More function evaluations
- **Stability**: Better numerical stability properties

## Interest Rate Models

### Short Rate Models

**Vasicek Model:**
dr(t) = κ(θ - r(t))dt + σ dW(t)

**Properties:**
- **Mean Reversion**: Interest rates revert to θ
- **Affine**: Bond prices are exponential affine in r
- **Gaussian**: Normal distribution for rates
- **Negative Rates**: Possible but bounded in probability

**Bond Pricing:**
P(t,T) = A(t,T)e^{-B(t,T)r(t)}

Where A(t,T) and B(t,T) are deterministic functions

**Hull-White Model:**
dr(t) = [θ(t) - ār(t)]dt + σ dW(t)

**Extensions:**
- **Time-Dependent Parameters**: θ(t) fits initial term structure
- **Market Completeness**: Perfect hedge using bonds
- **Calibration**: Match current yield curve exactly

### Multi-Factor Models

**Two-Factor Models:**
- **Long/Short Factors**: Separate short and long-term factors
- **Level/Slope**: Principal components of yield curve
- **Stochastic Mean**: Time-varying long-term level

**Heath-Jarrow-Morton (HJM):**
- **Forward Rate Evolution**: Model entire forward curve
- **No-Arbitrage**: Drift restrictions from no-arbitrage
- **Infinite Dimensions**: Forward rates for all maturities
- **Market Practice**: Foundation for interest rate derivatives

Stochastic processes provide the mathematical foundation for modern quantitative finance, enabling realistic modeling of financial markets and rigorous pricing of complex derivatives.`
        }
      ],
      quiz: {
        id: "stochastic-processes-quiz",
        title: "Stochastic Processes Quiz",
        description: "Test your understanding of stochastic processes in finance",
        timeLimit: 25,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the key property that makes Brownian motion suitable for modeling stock prices?",
            options: [
              "It has constant volatility",
              "It can take negative values",
              "It has independent increments and continuous paths",
              "It always trends upward"
            ],
            correctAnswer: 2,
            explanation: "Brownian motion has independent increments (memoryless property) and continuous paths, making it suitable for modeling the random walk behavior of stock prices."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In the Ornstein-Uhlenbeck process, what does the parameter κ represent?",
            options: [
              "The long-term mean",
              "The volatility",
              "The speed of mean reversion",
              "The initial value"
            ],
            correctAnswer: 2,
            explanation: "In the Ornstein-Uhlenbeck process, κ determines how quickly the process reverts to its long-term mean θ, with higher κ meaning faster reversion."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What is the main advantage of the Heston stochastic volatility model over Black-Scholes?",
            options: [
              "Simpler mathematical formulation",
              "Constant volatility assumption",
              "Captures volatility clustering and leverage effects",
              "No correlation between price and volatility"
            ],
            correctAnswer: 2,
            explanation: "The Heston model captures the empirical phenomena of volatility clustering and leverage effects (negative correlation between price and volatility changes) that Black-Scholes cannot model."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "quantitative-analysis-project",
      title: "Comprehensive Quantitative Analysis Project",
      description: "Apply quantitative methods to analyze a complex financial problem using statistical modeling and simulation techniques",
      type: "project",
      dueWeeks: 8,
      maxScore: 100,
      instructions: `# Quantitative Analysis Project

## Objective
Apply advanced quantitative methods to solve a real-world financial problem using statistical analysis, econometric modeling, and simulation techniques.

## Requirements
1. **Problem Definition**: Choose portfolio optimization, risk modeling, or derivatives pricing
2. **Data Analysis**: Statistical analysis of financial data with appropriate tests
3. **Model Development**: Build econometric or stochastic models
4. **Simulation**: Monte Carlo or other simulation techniques
5. **Validation**: Out-of-sample testing and robustness checks
6. **Implementation**: Practical recommendations with uncertainty analysis

## Deliverables
- Technical report with methodology and results (20-25 pages)
- Programming code with detailed documentation
- Data analysis and visualization exhibits
- Model validation and sensitivity analysis
- Executive summary for business stakeholders`,
      submissionFormat: "pdf",
      resources: [
        "Financial data sources and APIs",
        "Programming tutorials (Python/R/MATLAB)",
        "Academic papers on quantitative methods"
      ]
    }
  ],

  finalExam: {
    id: "quantitative-methods-final",
    title: "Quantitative Methods Final Exam",
    description: "Comprehensive examination of statistical and mathematical methods in finance",
    timeLimit: 150,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "In Monte Carlo simulation, increasing the number of simulations primarily:",
        options: [
          "Reduces bias in the estimates",
          "Reduces variance of the estimates",
          "Changes the expected value",
          "Eliminates model risk"
        ],
        correctAnswer: 1,
        explanation: "Increasing the number of Monte Carlo simulations reduces the variance (uncertainty) of the estimates by the law of large numbers, making the estimates more precise."
      }
    ]
  },

  quizzes: []
};